/**
 * The browser half of `docs/api/realtime-channels.json` (P0-WEB-05).
 *
 * That file is the cross-language contract: the Platform API broadcaster, this
 * client and the M0 gate all test against it rather than against each other.
 * The reason is that a broadcaster and a subscriber which disagree about a
 * channel or event name produce **no error on either side** — the browser
 * simply never receives anything, which looks exactly like a job that is still
 * running.
 *
 * `contract.test.ts` asserts every name here matches the file, in both
 * directions, so an event added to the contract and not handled here fails the
 * build instead of going quietly undelivered.
 */

export const CHANNELS = {
  /**
   * Everything that happens to one organisation's runs.
   *
   * Scoped to the organisation rather than to a single run, so a dashboard
   * holds one subscription rather than one per row, and a run created in
   * another tab still arrives.
   */
  organisationRuns: (organisationId: string) => `org.${organisationId}.runs`,
  userNotifications: (userId: string) => `user.${userId}.notifications`,
} as const;

export const RUN_EVENTS = {
  queued: 'runs.run_queued',
  started: 'runs.run_started',
  progressed: 'runs.run_progressed',
  succeeded: 'runs.run_succeeded',
  failed: 'runs.run_failed',
  cancelled: 'runs.run_cancelled',
} as const;

export const NOTIFICATION_EVENTS = {
  raised: 'notifications.notification_raised',
} as const;

export type RunEventName = (typeof RUN_EVENTS)[keyof typeof RUN_EVENTS];

export const RUN_STATUSES = ['queued', 'running', 'succeeded', 'failed', 'cancelled'] as const;

export type RunStatus = (typeof RUN_STATUSES)[number];

/**
 * A run in one of these will not change again.
 *
 * From the contract's client rules: "a client that only listens for
 * run_succeeded leaves a failed run spinning forever". Naming the whole set
 * once is what stops the next feature from listening for two of the three.
 */
export const TERMINAL_RUN_STATUSES: readonly RunStatus[] = ['succeeded', 'failed', 'cancelled'];

export function isTerminal(status: RunStatus): boolean {
  return TERMINAL_RUN_STATUSES.includes(status);
}

/**
 * Echo prepends the application's event namespace unless a name starts with a
 * dot. Ours are already fully qualified by the broadcaster (`runs.run_queued`),
 * so every subscription goes through here rather than remembering the dot at
 * six call sites — forgetting it subscribes to
 * `App.Events.runs.run_queued`, which nothing publishes.
 */
export function echoEventName(event: string): string {
  return `.${event}`;
}

/** The envelope the broadcaster adds to every payload. */
export interface EventEnvelope {
  /** RFC3339 with milliseconds. */
  readonly occurred_at: string;
}

export interface RunEventPayload extends EventEnvelope {
  readonly run_id: string;
  readonly status: RunStatus;
  readonly progress_pct?: number | null;
  readonly progress_message?: string | null;
  readonly progress_stage?: string | null;
  readonly credits_estimated?: number | null;
  readonly credits_charged?: number | null;
  readonly error_code?: string | null;
  readonly error_message?: string | null;
  readonly type?: string | null;
}

export interface NotificationEventPayload extends EventEnvelope {
  readonly notification_id: string;
  readonly type: string;
  readonly subject_type?: string | null;
  readonly subject_id?: string | null;
  /** Carries everything needed to render, so receipt never triggers a fetch. */
  readonly data?: Record<string, unknown>;
}

/**
 * Payloads arrive from the network, so they are checked rather than asserted.
 * A malformed one is dropped: applying an event with no `run_id` would create a
 * phantom run in the store that nothing can ever complete.
 */
export function isRunEventPayload(value: unknown): value is RunEventPayload {
  if (typeof value !== 'object' || value === null) return false;
  const payload = value as Record<string, unknown>;

  return (
    typeof payload.run_id === 'string' &&
    payload.run_id !== '' &&
    typeof payload.status === 'string' &&
    (RUN_STATUSES as readonly string[]).includes(payload.status) &&
    typeof payload.occurred_at === 'string'
  );
}
